import React, { useEffect, useState, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import { forecastRunsService } from '../services/forecastruns.service';

const ForecastChartApex = ({ runIds }) => {
  const [forecastData, setForecastData] = useState({});
  const [runInfo, setRunInfo] = useState({});
  const [stores, setStores] = useState([]);
  const [skus, setSkus] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const [visibleSeries, setVisibleSeries] = useState({
    actual: true,
    forecast: true,
    lower: false,
    upper: false
  });
  const [visibleRunIds, setVisibleRunIds] = useState(runIds);
  const [loading, setLoading] = useState(false);

  // Color scheme for different series
  const COLORS = {
    actual: '#000000',
    forecast: ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'],
    bounds: [
      ['#1976d2', '#42a5f5'],
      ['#2e7d32', '#66bb6a'],
      ['#f57c00', '#ffa726'],
      ['#d32f2f', '#ef5350'],
      ['#7b1fa2', '#ba68c8'],
      ['#00695c', '#4db6ac']
    ]
  };

  // Fetch data for all run IDs
  useEffect(() => {
    const fetchData = async () => {
      if (!runIds || runIds.length === 0) return;
      
      setLoading(true);
      try {
        const results = {};
        const runInfo = {};
        
        for (const runId of runIds) {
          try {
            // Fetch forecast data
            const response = await forecastRunsService.getForecastByRunId(runId);
            if (response.status === 'success') {
              results[runId] = response.forecasts.map(forecast => ({
                ...forecast,
                run_id: runId
              }));
            }
            
            // Fetch run info to get forecast start date
            const runInfoResponse = await forecastRunsService.getForecastRunById(runId);
            if (runInfoResponse.status === 'success' && runInfoResponse.data) {
              runInfo[runId] = runInfoResponse.data;
            }
          } catch (error) {
            console.error(`Failed to fetch data for runId ${runId}:`, error);
          }
        }
        
        setForecastData(results);
        setRunInfo(runInfo);
        
        // Extract unique stores and SKUs
        const allForecasts = Object.values(results).flat();
        const uniqueStores = [...new Set(allForecasts.map(f => f.store_id))];
        const uniqueSkus = [...new Set(allForecasts.map(f => f.sku))];
        
        setStores(uniqueStores);
        setSkus(uniqueSkus);
        
        // Set initial selections
        if (uniqueStores.length > 0) setSelectedStore(uniqueStores[0]);
        if (uniqueSkus.length > 0) setSelectedSku(uniqueSkus[0]);
      } catch (error) {
        console.error('Error fetching forecast data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [runIds]);

  // Process data for ApexCharts
  const chartData = useMemo(() => {
    if (!selectedStore || !selectedSku || !forecastData) return { series: [], categories: [] };

    const allData = [];
    const categories = new Set();

    // Get forecast start date from run info (use the earliest one if multiple runs)
    let forecastStartDate = null;
    let lastActualDate = null;
    let lastActualValue = null;
    
    // Find the earliest forecast start date from all visible runs
    visibleRunIds.forEach((runId) => {
      const runData = runInfo[runId];
      if (runData && runData.run_timestamp) {
        // Use run_timestamp as the forecast start date since forecast_start_date is defective
        const runForecastStart = new Date(runData.run_timestamp);
        // Round to start of day for cleaner date comparison
        const roundedForecastStart = new Date(runForecastStart.getFullYear(), runForecastStart.getMonth(), runForecastStart.getDate());
        if (!forecastStartDate || roundedForecastStart < forecastStartDate) {
          forecastStartDate = roundedForecastStart;
        }
      }
    });
    
    // If no forecast start date from run info, fall back to finding it from data
    if (!forecastStartDate) {
      visibleRunIds.forEach((runId) => {
        const forecasts = forecastData[runId] || [];
        const series = forecasts.find(f => f.store_id === selectedStore && f.sku === selectedSku);
        if (series && series.data.length > 0) {
          // Find the first forecast point (assuming data is sorted by date)
          for (let i = 0; i < series.data.length; i++) {
            const point = series.data[i];
            const date = new Date(point.date);
            
            // If this is the first point or if it's after the last actual point
            if (i === 0 || (lastActualDate && date > lastActualDate)) {
              if (!forecastStartDate || date < forecastStartDate) {
                forecastStartDate = date;
              }
              break;
            }
            lastActualDate = date;
            lastActualValue = point.value;
          }
        }
      });
    }

    // Calculate initial date range: 6 weeks before and after forecast start date
    let initialStartDate = new Date();
    let initialEndDate = new Date();
    
    if (forecastStartDate) {
      // 6 weeks = 42 days
      initialStartDate = new Date(forecastStartDate.getTime() - (42 * 24 * 60 * 60 * 1000));
      initialEndDate = new Date(forecastStartDate.getTime() + (42 * 24 * 60 * 60 * 1000));
      
      console.log('Forecast start date detected:', forecastStartDate.toISOString().split('T')[0]);
      console.log('Initial view range:', initialStartDate.toISOString().split('T')[0], 'to', initialEndDate.toISOString().split('T')[0]);
    } else {
      // Fallback: 3 months around current date
      const currentDate = new Date();
      initialStartDate = new Date(currentDate.getTime() - (90 * 24 * 60 * 60 * 1000));
      initialEndDate = new Date(currentDate.getTime() + (90 * 24 * 60 * 60 * 1000));
      console.log('No forecast start date found, using fallback range');
    }

    // Process each run
    visibleRunIds.forEach((runId, runIndex) => {
      const forecasts = forecastData[runId] || [];
      const series = forecasts.find(f => f.store_id === selectedStore && f.sku === selectedSku);
      
      if (!series) return;

      // Find the last actual data point for this run
      let lastActualPoint = null;
      for (let i = 0; i < series.data.length; i++) {
        const point = series.data[i];
        const date = new Date(point.date);
        if (forecastStartDate && date < forecastStartDate) {
          lastActualPoint = { date, value: point.value };
        } else {
          break;
        }
      }

      // Process data points
      series.data.forEach(point => {
        const date = new Date(point.date);
        categories.add(date.getTime());
        
        if (forecastStartDate && date < forecastStartDate) {
          // Actual data
          if (visibleSeries.actual) {
            allData.push({
              x: date.getTime(),
              y: point.value,
              series: `Actual (Run ${runId})`,
              type: 'actual',
              runId
            });
          }
        } else {
          // Forecast data - add padding point if this is the first forecast point
          const isFirstForecastPoint = lastActualPoint && date.getTime() === new Date(lastActualPoint.date.getTime() + 24 * 60 * 60 * 1000).getTime();
          
          if (visibleSeries.forecast) {
            // Add padding point to connect actual to forecast
            if (isFirstForecastPoint && lastActualPoint) {
              allData.push({
                x: lastActualPoint.date.getTime(),
                y: lastActualPoint.value,
                series: `Forecast (Run ${runId})`,
                type: 'forecast',
                runId
              });
            }
            
            allData.push({
              x: date.getTime(),
              y: point.value,
              series: `Forecast (Run ${runId})`,
              type: 'forecast',
              runId
            });
          }
          
          if (visibleSeries.lower) {
            // Add padding point for lower bound
            if (isFirstForecastPoint && lastActualPoint) {
              allData.push({
                x: lastActualPoint.date.getTime(),
                y: lastActualPoint.value,
                series: `Lower Bound (Run ${runId})`,
                type: 'lower',
                runId
              });
            }
            
            allData.push({
              x: date.getTime(),
              y: point.lower,
              series: `Lower Bound (Run ${runId})`,
              type: 'lower',
              runId
            });
          }
          
          if (visibleSeries.upper) {
            // Add padding point for upper bound
            if (isFirstForecastPoint && lastActualPoint) {
              allData.push({
                x: lastActualPoint.date.getTime(),
                y: lastActualPoint.value,
                series: `Upper Bound (Run ${runId})`,
                type: 'upper',
                runId
              });
            }
            
            allData.push({
              x: date.getTime(),
              y: point.upper,
              series: `Upper Bound (Run ${runId})`,
              type: 'upper',
              runId
            });
          }
        }
      });
    });

    // Group data by series
    const seriesMap = {};
    allData.forEach(point => {
      if (!seriesMap[point.series]) {
        seriesMap[point.series] = [];
      }
      seriesMap[point.series].push([point.x, point.y]);
    });

    // Convert to ApexCharts format
    const series = Object.entries(seriesMap).map(([name, data]) => {
      const runId = data[0]?.runId;
      const type = data[0]?.type;
      
      // Find the run index from visibleRunIds
      const runIndex = visibleRunIds.indexOf(runId);
      
      let color, dashArray;
      if (type === 'actual') {
        color = COLORS.actual;
      } else if (type === 'forecast') {
        color = COLORS.forecast[runIndex % COLORS.forecast.length];
      } else if (type === 'lower' || type === 'upper') {
        const [lowerColor, upperColor] = COLORS.bounds[runIndex % COLORS.bounds.length];
        color = type === 'lower' ? lowerColor : upperColor;
        dashArray = [4, 4];
      }

      return {
        name,
        data: data.sort((a, b) => a[0] - b[0]),
        color,
        dashArray,
        type: type === 'actual' ? 'line' : 'area',
        // Add area fill for forecast series (but not actual)
        fill: type !== 'actual' ? {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.3,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 100]
          }
        } : undefined,
        fillOpacity: type !== 'actual' ? 0.3 : undefined
      };
    });

    return {
      series,
      categories: Array.from(categories).sort((a, b) => a - b),
      initialStartDate,
      initialEndDate,
      forecastStartDate
    };
  }, [selectedStore, selectedSku, forecastData, visibleSeries, visibleRunIds]);

  // ApexCharts configuration
  const chartOptions = {
    chart: {
      type: 'line',
      height: '100%',
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    title: {
      text: `Forecast Comparison - SKU: ${selectedSku}, Store: ${selectedStore}`,
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: 'bold'
      }
    },
    xaxis: {
      type: 'datetime',
      title: {
        text: 'Date'
      },
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM \'yy',
          day: 'dd MMM',
          hour: 'HH:mm'
        }
      },
      // Set initial zoom range to 3 months (6 weeks before and after forecast start)
      range: undefined, // Will be set dynamically
      min: chartData.initialStartDate?.getTime(),
      max: chartData.initialEndDate?.getTime()
    },
    yaxis: {
      title: {
        text: 'Quantity'
      },
      labels: {
        formatter: (value) => Math.round(value)
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      },
      y: {
        formatter: (value) => Math.round(value)
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center'
    },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.3,
        opacityFrom: 0.4,
        opacityTo: 0.1,
        stops: [0, 100]
      }
    },
    markers: {
      size: 4,
      hover: {
        size: 6
      }
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5
      }
    },
    annotations: {
      xaxis: chartData.forecastStartDate ? [
        {
          x: chartData.forecastStartDate.getTime(),
          borderColor: '#ff6b6b',
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            borderColor: '#ff6b6b',
            style: {
              color: '#fff',
              background: '#ff6b6b'
            },
            text: 'Forecast Start'
          }
        }
      ] : []
    },
    plotOptions: {
      line: {
        curve: 'smooth'
      }
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: '100%'
          },
          legend: {
            position: 'bottom'
          }
        }
      }
    ]
  };

  const handleStoreChange = (event) => {
    setSelectedStore(event.target.value);
  };

  const handleSkuChange = (event) => {
    setSelectedSku(event.target.value);
  };

  const handleSeriesToggle = (seriesName) => {
    setVisibleSeries(prev => ({
      ...prev,
      [seriesName]: !prev[seriesName]
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Typography>Loading forecast data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Controls */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        {/* Store and SKU selectors */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Store</InputLabel>
            <Select value={selectedStore} onChange={handleStoreChange} label="Store">
              {stores.map(store => (
                <MenuItem key={store} value={store}>Store {store}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>SKU</InputLabel>
            <Select value={selectedSku} onChange={handleSkuChange} label="SKU">
              {skus.map(sku => (
                <MenuItem key={sku} value={sku}>SKU {sku}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Series visibility controls */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(visibleSeries).map(([name, visible]) => (
            <Chip
              key={name}
              label={name.charAt(0).toUpperCase() + name.slice(1)}
              onClick={() => handleSeriesToggle(name)}
              color={visible ? 'primary' : 'default'}
              variant={visible ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>

        {/* Run ID visibility controls */}
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Run IDs</InputLabel>
          <Select
            multiple
            value={visibleRunIds}
            onChange={(event) => setVisibleRunIds(event.target.value)}
            input={<OutlinedInput label="Run IDs" />}
            renderValue={(selected) => selected.join(', ')}
          >
            {runIds.map((runId) => (
              <MenuItem key={runId} value={runId}>
                <Checkbox checked={visibleRunIds.includes(runId)} />
                <ListItemText primary={`Run ${runId}`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Chart */}
      <Box sx={{ 
        border: '1px solid #e0e0e0', 
        borderRadius: 1, 
        p: 2,
        backgroundColor: '#fff',
        height: '500px',
        minHeight: '400px'
      }}>
        <ReactApexChart
          options={chartOptions}
          series={chartData.series}
          type="line"
          height="100%"
        />
      </Box>

      {/* Zoom Instructions */}
      <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          💡 Use mouse wheel to zoom, drag to pan, or use the toolbar controls above the chart. 
          Shaded areas indicate forecast periods, and the red line marks the forecast start date.
        </Typography>
      </Box>
    </Box>
  );
};

export default ForecastChartApex; 